/**
 * /architect/run/$runId — Workstream view for a single Architect run.
 *
 * Layout (left-to-right inside main canvas):
 *   ┌─────────────────────────────────────────┬───────────────┐
 *   │ Header (title + status + duration)       │               │
 *   ├─────────────────────────────────────────┤  Node detail  │
 *   │ Workstream graph (SVG, 9 nodes)         │  panel        │
 *   ├─────────────────────────────────────────┤               │
 *   │ Decomposed cards (links to /board/...)  │               │
 *   ├─────────────────────────────────────────┤               │
 *   │ Footer: Cancel / Rerun                  │               │
 *   └─────────────────────────────────────────┴───────────────┘
 *
 * Real-time: useArchitectRunStream subscribes to the per-run WS; every
 * NOTIFY invalidates `architectKeys.run(runId)` so the graph + table
 * re-render automatically.
 */
import type { ArchitectNode, ArchitectRun } from '@chaos/types/architect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { Loader2, RotateCcw, X } from 'lucide-react';
import * as React from 'react';

import { NodeDetailPanel } from '../../../../components/architect/node-detail-panel';
import { WorkstreamGraph } from '../../../../components/architect/workstream-graph';
import { Button } from '../../../../components/ui/button';
import { architectApi, architectKeys } from '../../../../lib/api/architect';
import { useArchitectRunStream } from '../../../../lib/architect/use-run-stream';
import { cn } from '../../../../lib/utils';

function statusBadgeClass(status: ArchitectRun['status']): string {
  switch (status) {
    case 'succeeded':
      return 'bg-success/15 text-success';
    case 'running':
      return 'bg-foreground/8 text-foreground';
    case 'failed':
      return 'bg-error/15 text-error';
    case 'cancelled':
      return 'bg-warning/15 text-warning';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function formatDuration(start: string, end: string | null): string {
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return '—';
  const delta = Math.max(0, endMs - startMs);
  if (delta < 1_000) return `${delta}ms`;
  if (delta < 60_000) return `${(delta / 1_000).toFixed(1)}s`;
  return `${Math.floor(delta / 60_000)}m ${Math.floor((delta % 60_000) / 1_000)}s`;
}

function RunDetailPage(): React.JSX.Element {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const runQ = useQuery({
    queryKey: architectKeys.run(runId),
    queryFn: () => architectApi.getRun(runId),
  });

  // Subscribe to WS NOTIFY → cache invalidation.
  useArchitectRunStream(runId);

  // Pre-select the first running / failed node so the inspector isn't empty.
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!runQ.data) return;
    if (selectedId && runQ.data.nodes.some((n) => n.id === selectedId)) return;
    const running = runQ.data.nodes.find((n) => n.state === 'running');
    const failed = runQ.data.nodes.find((n) => n.state === 'failed');
    const lastDone = [...runQ.data.nodes].reverse().find((n) => n.state === 'done');
    const first = runQ.data.nodes[0];
    const next = running ?? failed ?? lastDone ?? first ?? null;
    setSelectedId(next ? next.id : null);
  }, [runQ.data, selectedId]);

  const selectedNode: ArchitectNode | null = React.useMemo(() => {
    if (!runQ.data || !selectedId) return null;
    return runQ.data.nodes.find((n) => n.id === selectedId) ?? null;
  }, [runQ.data, selectedId]);

  const cancelM = useMutation({
    mutationFn: () => architectApi.cancelRun(runId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: architectKeys.run(runId) });
      await qc.invalidateQueries({ queryKey: architectKeys.all });
    },
  });

  const rerunM = useMutation({
    mutationFn: () => architectApi.rerunRun(runId),
    onSuccess: async (ack) => {
      await qc.invalidateQueries({ queryKey: architectKeys.all });
      await navigate({ to: '/architect/run/$runId', params: { runId: ack.runId } });
    },
  });

  if (runQ.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (runQ.error || !runQ.data) {
    return (
      <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
        Run not found.
      </div>
    );
  }

  const run = runQ.data;
  const inProgress = run.status === 'pending' || run.status === 'running';

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-col gap-2 border-b border-border px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <Link
                to="/architect"
                className="text-[12px] text-muted-foreground hover:text-foreground"
              >
                Architect
              </Link>
              <span className="text-[12px] text-muted-foreground/60">/</span>
              <span className="truncate text-[13px] text-foreground">
                {run.description.length > 80 ? `${run.description.slice(0, 79)}…` : run.description}
              </span>
              {run.dryRun && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  dry-run
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                  statusBadgeClass(run.status),
                )}
              >
                {run.status}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {formatDuration(run.startedAt, run.endedAt)}
              </span>
            </div>
          </div>
          {run.errorMessage && (
            <p
              role="alert"
              className="rounded border border-error/40 bg-error/10 px-3 py-2 text-[12px] text-error"
            >
              {run.errorMessage}
            </p>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4">
          <section className="mb-6">
            <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Workflow
            </h2>
            <WorkstreamGraph
              nodes={run.nodes}
              selectedNodeId={selectedId}
              onSelect={setSelectedId}
            />
          </section>

          <section>
            <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Decomposed cards
            </h2>
            {run.decomposedCardIds.length === 0 ? (
              <p className="text-[12px] text-muted-foreground/70">
                {inProgress
                  ? 'Cards will appear as the agent dispatches them.'
                  : 'No cards created.'}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {run.decomposedCardIds.map((id) => (
                  <li key={id}>
                    <span className="font-mono text-[11px] text-muted-foreground">{id}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border px-6 py-3">
          {inProgress ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => cancelM.mutate()}
              disabled={cancelM.isPending}
              className="gap-1.5"
            >
              {cancelM.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <X className="h-3.5 w-3.5" aria-hidden />
              )}
              Cancel
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => rerunM.mutate()}
              disabled={rerunM.isPending}
              className="gap-1.5"
            >
              {rerunM.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              )}
              Rerun
            </Button>
          )}
        </footer>
      </div>

      <aside className="hidden w-[360px] shrink-0 border-l border-border lg:flex lg:flex-col">
        <NodeDetailPanel node={selectedNode} />
      </aside>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/architect/run/$runId')({
  component: RunDetailPage,
});
